const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/
const LOCATION_SDP_REGEX = /:sdp:[^:]+$/i
const MONTHS_IN_RESPONSE = 12
const TREND_LOOKBACK_MONTHS = 3

function checkForRequirements (pgConnection, location) {
  if (!pgConnection) {
    const err = new Error('Postgres connection is required')
    err.status = 500
    throw err
  }

  if (!location) {
    const err = new Error('location is required')
    err.status = 400
    throw err
  }
}

function parseDateOnlyUtcOrThrow (value, fieldName) {
  if (!DATE_ONLY_REGEX.test(value)) {
    throw new Error(`${fieldName} must be in YYYY-MM-DD format`)
  }

  const date = new Date(`${value}T00:00:00.000Z`)
  if (Number.isNaN(date.getTime())) {
    throw new Error(`${fieldName} is not a valid date`)
  }

  if (date.toISOString().slice(0, 10) !== value) {
    throw new Error(`${fieldName} is not a valid date`)
  }

  return date
}

function resolveLocationScope (location) {
  if (location === 'national') {
    return {
      location: 'national',
      scopeType: 'national'
    }
  }

  const scopeType = LOCATION_SDP_REGEX.test(location) ? 'sdp' : 'regional'
  return {
    location: location,
    scopeType: scopeType
  }
}

function buildAnalyticsBaseContext ({ fromDate, toDate, location }) {
  const fromDateUtc = parseDateOnlyUtcOrThrow(fromDate, 'fromDate')
  const toDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
  const toDateExclusiveUtc = new Date(toDateUtc)
  toDateExclusiveUtc.setUTCDate(toDateExclusiveUtc.getUTCDate() + 1)

  return {
    fromDateUtc: fromDateUtc,
    toDateUtc: toDateUtc,
    toDateExclusiveUtc: toDateExclusiveUtc,
    locationScope: resolveLocationScope(location)
  }
}

function buildIndicatorQueryContext ({ organisationId, fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope, productSku, serviceIds }) {
  const currentMonthStartUtc = new Date(Date.UTC(fromDateUtc.getUTCFullYear(), fromDateUtc.getUTCMonth(), 1))
  const previous3MonthsStartUtc = new Date(Date.UTC(currentMonthStartUtc.getUTCFullYear(), currentMonthStartUtc.getUTCMonth() - 3, 1))
  // The trend delta always compares the current month against the prior 3 months, so its
  // "current" side is normalised over month-to-date — independent of the selected reporting
  // period, which may be a single day for daily tenants (RMS) or a full month (EPN).
  const currentMonthDays = calculateInclusiveDays(currentMonthStartUtc, toDateUtc)
  const previous3MonthsPeriodDays = calculateInclusiveDays(previous3MonthsStartUtc, new Date(currentMonthStartUtc.getTime() - (24 * 60 * 60 * 1000)))
  const params = [
    organisationId, // $1
    previous3MonthsStartUtc.toISOString(), // $2 trend: prior-3-months start
    toDateExclusiveUtc.toISOString(), // $3 outer filter end (toDate + 1 day)
    currentMonthStartUtc.toISOString(), // $4 trend: current-month start
    toDateExclusiveUtc.toISOString(), // $5 window end (toDate + 1 day)
    fromDateUtc.toISOString() // $6 selected reporting-period start
  ]
  const whereClauses = []

  appendScopeAndProductFilters({ params, whereClauses, locationScope, productSku, serviceIds })

  return {
    currentMonthDays: currentMonthDays,
    previous3MonthsPeriodDays: previous3MonthsPeriodDays,
    params: params,
    whereClauses: whereClauses
  }
}

function buildMonthlyIndicatorDateWindow ({ toDate, location }) {
  const selectedToDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
  const toMonthStartUtc = new Date(Date.UTC(selectedToDateUtc.getUTCFullYear(), selectedToDateUtc.getUTCMonth(), 1))
  const toDateExclusiveUtc = new Date(Date.UTC(toMonthStartUtc.getUTCFullYear(), toMonthStartUtc.getUTCMonth() + 1, 1))
  const toDateUtc = new Date(Date.UTC(toMonthStartUtc.getUTCFullYear(), toMonthStartUtc.getUTCMonth() + 1, 0))
  const fromDateUtc = new Date(Date.UTC(toMonthStartUtc.getUTCFullYear(), toMonthStartUtc.getUTCMonth() - (MONTHS_IN_RESPONSE - 1), 1))

  return {
    fromDate: fromDateUtc.toISOString().slice(0, 10),
    fromDateUtc: fromDateUtc,
    toDateUtc: toDateUtc,
    toDateExclusiveUtc: toDateExclusiveUtc,
    locationScope: resolveLocationScope(location)
  }
}

function buildMonthlyIndicatorQueryContext ({ organisationId, fromDateUtc, toDateExclusiveUtc, locationScope, productSku, serviceIds }) {
  const trendWindowStartUtc = new Date(Date.UTC(fromDateUtc.getUTCFullYear(), fromDateUtc.getUTCMonth() - TREND_LOOKBACK_MONTHS, 1))
  const params = [
    organisationId,
    trendWindowStartUtc.toISOString(),
    toDateExclusiveUtc.toISOString()
  ]
  const whereClauses = []

  appendScopeAndProductFilters({ params, whereClauses, locationScope, productSku, serviceIds })

  return {
    params: params,
    whereClauses: whereClauses
  }
}

function appendProductAndServiceFilters ({ params, whereClauses, productSku, serviceIds }) {
  if (productSku) {
    whereClauses.push(`AND si.product_sku_id = $${params.length + 1}`)
    params.push(productSku)
  }

  if (serviceIds && serviceIds.length > 0) {
    whereClauses.push(`AND si.service_id = ANY($${params.length + 1}::uuid[])`)
    params.push(serviceIds)
  }
}

function appendScopeAndProductFilters ({ params, whereClauses, locationScope, productSku, serviceIds }) {
  if (locationScope.scopeType !== 'national') {
    const exactIndex = params.length + 1
    const descendantIndex = params.length + 2
    whereClauses.push(`
        AND (
          dl.fsid = $${exactIndex}
          OR dl.fsid LIKE $${descendantIndex}
        )
    `)
    params.push(locationScope.location, `${locationScope.location}:%`)
  }

  appendProductAndServiceFilters({ params, whereClauses, productSku, serviceIds })
}

// if aggregationLevel is 'warehouse' (RMS), always group by individual warehouse (:name: level),
// regardless of whether location is national, a zone, a state, etc. - the WHERE clause
// (appendScopeAndProductFilters) already scopes rows down to that location, so grouping just
// needs to bucket whatever comes back by warehouse.
// otherwise (EPN-style district hierarchy):
// if location is national, then
// - if the location starts with zone:, then group by zone
// - if the location starts with country:, then group by country + next key:value (eg. state)
// - fallback is to group by state
// if the location is zone (zone:* or country:*:zone:*) then group by state
// if the location is state then group by lga
// if the location is lga then group by sdp
function getLocationTableGrouping (location, aggregationLevel) {
  if (aggregationLevel === 'warehouse') {
    return {
      groupByLevel: 'name',
      scopeExpression: `substring(dl.fsid from '^(.*:name:[^:]+)')`
    }
  }

  if (location === 'national') {
    return {
      groupByLevel: 'state',
      scopeExpression: `
        CASE
          WHEN dl.fsid ~* '^zone:[^:]+' THEN substring(dl.fsid from '^(zone:[^:]+)')
          WHEN dl.fsid ~* '^country:[^:]+:[^:]+:[^:]+' THEN substring(dl.fsid from '^(country:[^:]+:[^:]+:[^:]+)')
          ELSE substring(dl.fsid from '^(.*:state:[^:]+)')
        END
      `
    }
  }

  if (/^(?:country:[^:]+:)?zone:[^:]+$/i.test(location)) {
    return {
      groupByLevel: 'state',
      scopeExpression: `substring(dl.fsid from '^(.*:state:[^:]+)')`
    }
  }

  if (/:state:[^:]+$/i.test(location)) {
    return {
      groupByLevel: 'lga',
      scopeExpression: `substring(dl.fsid from '^(.*:lga:[^:]+)')`
    }
  }

  if (/:lga:[^:]+$/i.test(location)) {
    return {
      groupByLevel: 'sdp',
      scopeExpression: `substring(dl.fsid from '^(.*:sdp:[^:]+)')`
    }
  }

  const err = new Error('location must be national, state, zone or lga scope for the location table')
  err.status = 400
  throw err
}

async function resolveServiceIds (pgConnection, programs) {
  if (!programs || programs.length === 0) return null
  const { rows } = await pgConnection.query(
    `SELECT ds.id FROM avocado.data_service ds
       JOIN avocado.data_program dp ON dp.id = ds.program_id
       WHERE dp.fsid = ANY($1)`,
    [programs]
  )
  return rows.map(r => r.id)
}

function getLocationLeafExpressions (aggregationLevel) {
  const leafKey = aggregationLevel === 'warehouse' ? 'name' : 'sdp'

  return {
    leafFilterExpression: `position(':${leafKey}:' in dl.fsid) > 0`,
    displayNameExpression: `
      COALESCE(
        NULLIF(dl.name_of_pharmacy, ''),
        replace(split_part(dl.fsid, ':${leafKey}:', 2), '-', ' '),
        dl.fsid
      )
    `
  }
}

function calculateInclusiveDays (fromDateUtc, toDateUtc) {
  const msPerDay = 24 * 60 * 60 * 1000
  return Math.floor((toDateUtc.getTime() - fromDateUtc.getTime()) / msPerDay) + 1
}

function getNationalGroupKeyFromLocationFsid (locationFsid) {
  const fsid = String(locationFsid || '')
  const zoneMatch = fsid.match(/^(zone:[^:]+)/i)
  if (zoneMatch) {
    return zoneMatch[1]
  }

  const countryMatch = fsid.match(/^(country:[^:]+:[^:]+:[^:]+)/i)
  if (countryMatch) {
    return countryMatch[1]
  }

  return fsid
}

function getCurrentUtcMonthKey () {
  const now = new Date()
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`
}

function calculateDeltaPct (currentValue, baselineValue) {
  if (baselineValue === 0) {
    return null
  }
  return roundTo2(((currentValue - baselineValue) / baselineValue) * 100)
}

function roundTo2 (value) {
  return Math.round(value * 100) / 100
}

module.exports = {
  MONTHS_IN_RESPONSE,
  TREND_LOOKBACK_MONTHS,
  checkForRequirements,
  parseDateOnlyUtcOrThrow,
  resolveLocationScope,
  buildAnalyticsBaseContext,
  buildIndicatorQueryContext,
  buildMonthlyIndicatorDateWindow,
  buildMonthlyIndicatorQueryContext,
  appendScopeAndProductFilters,
  appendProductAndServiceFilters,
  getLocationTableGrouping,
  getLocationLeafExpressions,
  resolveServiceIds,
  getNationalGroupKeyFromLocationFsid,
  getCurrentUtcMonthKey,
  calculateInclusiveDays,
  calculateDeltaPct,
  roundTo2
}
